const DEFAULT_LANGUAGE = 'en-US';

const defaultFormatOptions = (currency: string): Intl.NumberFormatOptions => ({
  style: 'currency',
  currencyDisplay: 'symbol',
  currencySign: 'standard',
  notation: 'standard',
  currency,
});

const currencyFormatter =
  (currency: string, language: string = DEFAULT_LANGUAGE) =>
  (options: Intl.NumberFormatOptions = {}) =>
    new Intl.NumberFormat(language, {
      ...defaultFormatOptions(currency),
      ...options,
    });

type CurrencyObject = {
  code: string;
  name: string;
  symbol: string;
  currencySymbol: string;
  makeFormatter: (options?: Intl.NumberFormatOptions) => Intl.NumberFormat;
};

const makeCurrencyObject = (
  code: string,
  name: string,
  symbol: string | null = null,
  currencySymbol: string | null = null
): CurrencyObject => ({
  code,
  name,
  symbol: symbol || code,
  currencySymbol: currencySymbol || code,
  makeFormatter: currencyFormatter(code),
});

const currencyInfo = [
  ['ADP', 'Andorran peseta'],
  ['AED', 'UAE dirham'],
  ['AFA', 'Afghan afghani (1927–2002)'],
  ['AFN', 'Afghan Afghani', '؋'],
  ['ALK', 'Albanian lek (1946–1965)'],
  ['ALL', 'Albanian lek', 'LEK'],
  ['AMD', 'Armenian dram'],
  ['ANG', 'Netherlands Antillean guilder', 'ƒ'],
  ['AOA', 'Angolan kwanza', 'Kz', 'Kz'],
  ['AOK', 'Angolan kwanza (1977–1991)'],
  ['AON', 'Angolan new kwanza (1990–2000)'],
  ['AOR', 'Angolan readjusted kwanza (1995–1999)'],
  ['ARA', 'Argentine austral'],
  ['ARL', 'Argentine peso ley (1970–1983)'],
  ['ARM', 'Argentine peso (1881–1970)'],
  ['ARP', 'Argentine peso (1983–1985)'],
  ['ARS', 'Argentine peso', '$', '$'],
  ['ATS', 'Austrian schilling'],
  ['AUD', 'Australian dollar', '$', 'A$'],
  ['AWG', 'Aruban florin', 'ƒ'],
  ['AZM', 'Azerbaijani manat (1993–2006)'],
  ['AZN', 'Azerbaijani manat', 'ман'],
  ['BAD', 'Bosnia-Herzegovina dinar (1992–1994)'],
  ['BAM', 'Bosnia-Herzegovina convertible mark', 'KM', 'KM'],
  ['BAN', 'Bosnia-Herzegovina new dinar (1994–1997)'],
  ['BBD', 'Barbadian dollar', '$', '$'],
  ['BDT', 'Bangladeshi taka', '৳', '৳'],
  ['BEC', 'Belgian franc (convertible)'],
  ['BEF', 'Belgian franc'],
  ['BEL', 'Belgian franc (financial)'],
  ['BGL', 'Bulgarian hard lev'],
  ['BGM', 'Bulgarian socialist lev'],
  ['BGN', 'Bulgarian lev', 'лв'],
  ['BGO', 'Bulgarian lev (1879–1952)'],
  ['BHD', 'Bahraini dinar'],
  ['BIF', 'Burundian franc'],
  ['BMD', 'Bermudan dollar', '$', '$'],
  ['BND', 'Brunei dollar', '$', '$'],
  ['BOB', 'Bolivian boliviano', '$b', 'Bs'],
  ['BOL', 'Bolivian boliviano (1863–1963)'],
  ['BOP', 'Bolivian peso'],
  ['BOV', 'Bolivian mvdol'],
  ['BRB', 'Brazilian new cruzeiro (1967–1986)'],
  ['BRC', 'Brazilian cruzado (1986–1989)'],
  ['BRE', 'Brazilian cruzeiro (1990–1993)'],
  ['BRL', 'Brazilian real', 'R$', 'R$'],
  ['BRN', 'Brazilian new cruzado (1989–1990)'],
  ['BRR', 'Brazilian cruzeiro (1993–1994)'],
  ['BRZ', 'Brazilian cruzeiro (1942–1967)'],
  ['BSD', 'Bahamian dollar', '$', '$'],
  ['BTN', 'Bhutanese ngultrum'],
  ['BUK', 'Burmese kyat'],
  ['BWP', 'Botswanan pula', 'P', 'P'],
  ['BYB', 'Belarusian new ruble (1994–1999)'],
  ['BYN', 'Belarusian ruble', 'Br', 'Br'],
  ['BYR', 'Belarusian ruble', 'p.', 'р.'],
  ['BZD', 'Belize dollar', 'BZ$', '$'],
  ['CAD', 'Canadian dollar', '$', 'CA$'],
  ['CDF', 'Congolese franc'],
  ['CHE', 'WIR euro'],
  ['CHF', 'Swiss franc'],
  ['CHW', 'WIR franc'],
  ['CLE', 'Chilean escudo'],
  ['CLF', 'Chilean unit of account (UF)'],
  ['CLP', 'Chilean peso', '$', '$'],
  ['CNX', 'Chinese People’s Bank dollar'],
  ['CNY', 'Chinese yuan', '¥', 'CN¥'],
  ['COP', 'Colombian peso', '$', '$'],
  ['COU', 'Colombian real value unit'],
  ['CRC', 'Costa Rican colón', '₡', '₡'],
  ['CSD', 'Serbian dinar (2002–2006)'],
  ['CSK', 'Czechoslovak hard koruna'],
  ['CUC', 'Cuban convertible peso', '$', '$'],
  ['CUP', 'Cuban peso', '₱', '$'],
  ['CVE', 'Cape Verdean escudo'],
  ['CYP', 'Cypriot pound'],
  ['CZK', 'Czech Republic koruna', 'Kč', 'Kč'],
  ['DDM', 'East German mark'],
  ['DEM', 'German mark'],
  ['DJF', 'Djiboutian franc'],
  ['DKK', 'Danish krone', 'kr', 'kr'],
  ['DOP', 'Dominican peso', 'RD$', '$'],
  ['DZD', 'Algerian dinar'],
  ['ECS', 'Ecuadorian sucre'],
  ['ECV', 'Ecuadorian unit of constant value'],
  ['EEK', 'Estonian kroon', 'kr'],
  ['EGP', 'Egyptian pound', '£', 'E£'],
  ['ERN', 'Eritrean nakfa'],
  ['ESA', 'Spanish peseta (A account)'],
  ['ESB', 'Spanish peseta (convertible account)'],
  ['ESP', 'Spanish Peseta', '₧', '₧'],
  ['ETB', 'Ethiopian Birr'],
  ['EUR', 'Euro', '€', '€'],
  ['FIM', 'Finnish Markka'],
  ['FJD', 'Fijian Dollar', '$', '$'],
  ['FKP', 'Falkland Islands Pound', '£', '£'],
  ['FRF', 'French Franc'],
  ['GBP', 'British Pound Sterling', '£', '£'],
  ['GEK', 'Georgian kupon larit'],
  ['GEL', 'Georgian lari'],
  ['GHC', 'Ghanaian cedi (1979–2007)'],
  ['GGP', 'Guernsey pound', '£', '£'],
  ['GHS', 'Ghanaian cedi', '¢'],
  ['GIP', 'Gibraltar pound', '£', '£'],
  ['GMD', 'Gambian dalasi'],
  ['GNF', 'Guinean franc', 'FG', 'FG'],
  ['GNS', 'Guinean syli'],
  ['GQE', 'Equatorial Guinean ekwele'],
  ['GRD', 'Greek drachma'],
  ['GTQ', 'Guatemalan quetzal', 'Q', 'Q'],
  ['GWE', 'Portuguese Guinea escudo'],
  ['GWP', 'Guinea-Bissau peso'],
  ['GYD', 'Guyanaese dollar', '$', '$'],
  ['HKD', 'Hong Kong dollar', '$', 'HK$'],
  ['HNL', 'Honduran lempira', 'L', 'L'],
  ['HRD', 'Croatian dinar'],
  ['HRK', 'Croatian kuna', 'kn', 'kn'],
  ['HTG', 'Haitian gourde'],
  ['HUF', 'Hungarian forint', 'Ft', 'Ft'],
  ['IDR', 'Indonesian Rupiah', 'Rp', 'Rp'],
  ['IEP', 'Irish pound'],
  ['ILP', 'Israeli Pound'],
  ['ILR', 'Israeli Sheqel (1980–1985)'],
  ['ILS', 'Israeli New Sheqel', '₪', '₪'],
  ['IMP', 'Manx pound', '£', '£'],
  ['INR', 'Indian Rupee', '₨', '₹'],
  ['IQD', 'Iraqi dinar'],
  ['IRR', 'Iranian rial', '﷼'],
  ['ISJ', 'Icelandic króna (1918–1981)'],
  ['ISK', 'Icelandic króna', 'kr', 'kr'],
  ['ITL', 'Italian lira'],
  ['JEP', 'Jersey pound', '£', '£'],
  ['JMD', 'Jamaican dollar', '$', '$'],
  ['JOD', 'Jordanian dinar'],
  ['JPY', 'Japanese yen', '¥', '¥'],
  ['KES', 'Kenyan shilling'],
  ['KGS', 'Kyrgystani som', 'лв'],
  ['KHR', 'Cambodian riel', '៛', '៛'],
  ['KMF', 'Comorian franc', 'CF', 'CF'],
  ['KPW', 'North Korean won', '₩', '₩'],
  ['KRH', 'South Korean hwan (1953–1962)'],
  ['KRO', 'South Korean won (1945–1953)'],
  ['KRW', 'South Korean won', '₩', '₩'],
  ['KWD', 'Kuwaiti dinar'],
  ['KYD', 'Cayman Islands dollar', '$', '$'],
  ['KZT', 'Kazakhstani tenge', 'лв', '₸'],
  ['LAK', 'Laotian kip', '₭', '₭'],
  ['LBP', 'Lebanese Pound', '£', 'L£'],
  ['LKR', 'Sri Lankan rupee', '₨', 'Rs'],
  ['LRD', 'Liberian dollar', '$', '$'],
  ['LSL', 'Lesotho loti'],
  ['LTL', 'Lithuanian litas', 'Lt', 'Lt'],
  ['LTT', 'Lithuanian talonas'],
  ['LUC', 'Luxembourgian convertible franc'],
  ['LUF', 'Luxembourgian franc'],
  ['LUL', 'Luxembourg financial franc'],
  ['LVL', 'Latvian lats', 'Ls', 'Ls'],
  ['LVR', 'Latvian ruble'],
  ['LYD', 'Libyan dinar'],
  ['MAD', 'Moroccan dirham'],
  ['MAF', 'Moroccan franc'],
  ['MCF', 'Monegasque franc'],
  ['MDC', 'Moldovan cupon'],
  ['MDL', 'Moldovan leu'],
  ['MGA', 'Malagasy Ariary', 'Ar', 'Ar'],
  ['MGF', 'Malagasy franc'],
  ['MKD', 'Macedonian denar'],
  ['MKN', 'Macedonian denar (1992–1993)'],
  ['MLF', 'Malian franc'],
  ['MMK', 'Myanmar kyat', 'K', 'K'],
  ['MNT', 'Mongolian tugrik', '₮', '₮'],
  ['MOP', 'Macanese pataca'],
  ['MRO', 'Mauritanian ouguiya'],
  ['MTL', 'Maltese lira'],
  ['MTP', 'Maltese pound'],
  ['MUR', 'Mauritian rupee', '₨', 'Rs'],
  ['MVP', 'Maldivian rupee (1947–1981)'],
  ['MVR', 'Maldivian rufiyaa'],
  ['MWK', 'Malawian Kwacha'],
  ['MXN', 'Mexican peso', '$', 'MX$'],
  ['MXP', 'Mexican silver peso (1861–1992)'],
  ['MXV', 'Mexican investment unit'],
  ['MYR', 'Malaysian ringgit', 'RM', 'RM'],
  ['MZE', 'Mozambican escudo'],
  ['MZM', 'Mozambican metical (1980–2006)'],
  ['MZN', 'Mozambican metical', 'MT'],
  ['NAD', 'Namibian dollar', '$', '$'],
  ['NGN', 'Nigerian naira', '₦', '₦'],
  ['NIC', 'Nicaraguan córdoba (1988–1991)'],
  ['NIO', 'Nicaraguan córdoba', 'C$', 'C$'],
  ['NLG', 'Dutch guilder'],
  ['NOK', 'Norwegian krone', 'kr', 'kr'],
  ['NPR', 'Nepalese rupee', '₨', 'Rs'],
  ['NZD', 'New Zealand dollar', '$', 'NZ$'],
  ['OMR', 'Omani rial', '﷼'],
  ['PAB', 'Panamanian balboa', 'B/.'],
  ['PEI', 'Peruvian inti'],
  ['PEN', 'Peruvian nuevo sol', 'S/.'],
  ['PES', 'Peruvian sol (1863–1965)'],
  ['PGK', 'Papua New Guinean kina'],
  ['PHP', 'Philippine peso', 'Php', '₱'],
  ['PKR', 'Pakistani rupee', '₨', 'Rs'],
  ['PLN', 'Polish zloty', 'zł', 'zł'],
  ['PLZ', 'Polish zloty (PLZ)'],
  ['PTE', 'Portuguese escudo'],
  ['PYG', 'Paraguayan guarani', 'Gs', '₲'],
  ['QAR', 'Qatari rial', '﷼'],
  ['RHD', 'Rhodesian dollar'],
  ['ROL', 'Romanian leu (1952–2006)'],
  ['RON', 'Romanian leu', 'lei'],
  ['RSD', 'Serbian dinar', 'Дин.'],
  ['RUB', 'Russian ruble', 'руб', '₽'],
  ['RUR', 'Russian ruble (1991–1998)', 'р.', 'р.'],
  ['RWF', 'Rwandan franc', 'RF', 'RF'],
  ['SAR', 'Saudi riyal', '﷼'],
  ['SBD', 'Solomon Islands dollar', '$', '$'],
  ['SCR', 'Seychellois rupee', '₨'],
  ['SDD', 'Sudanese dinar (1992–2007)'],
  ['SDG', 'Sudanese pound'],
  ['SDP', 'Sudanese pound (1957–1998)'],
  ['SEK', 'Swedish krona', 'kr', 'kr'],
  ['SGD', 'Singapore dollar', '$', '$'],
  ['SHP', 'St. Helena pound', '£', '£'],
  ['SIT', 'Slovenian tolar'],
  ['SKK', 'Slovak koruna'],
  ['SLL', 'Sierra Leonean leone'],
  ['SOS', 'Somali shilling', 'S'],
  ['SRD', 'Surinamese dollar', '$', '$'],
  ['SRG', 'Surinamese guilder'],
  ['SSP', 'South Sudanese pound', '£', '£'],
  ['STD', 'São Tomé & Príncipe dobra', 'Db', 'Db'],
  ['SUR', 'Soviet rouble'],
  ['SVC', 'Salvadoran colón'],
  ['SYP', 'Syrian pound', '£', '£'],
  ['SZL', 'Swazi lilangeni'],
  ['THB', 'Thai baht', '฿', '฿'],
  ['TJR', 'Tajikistani ruble'],
  ['TJS', 'Tajikistani somoni'],
  ['TMM', 'Turkmenistani manat (1993–2009)'],
  ['TMT', 'Turkmenistani manat'],
  ['TND', 'Tunisian dinar'],
  ['TOP', 'Tongan paʻanga', 'T$', 'T$'],
  ['TPE', 'Timorese escudo'],
  ['TRL', 'Turkish lira (1922–2005)'],
  ['TRY', 'Turkish lira', 'TL', '₺'],
  ['TTD', 'Trinidad & Tobago dollar', '$', '$'],
  ['TWD', 'New Taiwan dollar', 'NT$', 'NT$'],
  ['TZS', 'Tanzanian shilling'],
  ['UAH', 'Ukrainian hryvnia', '₴', '₴'],
  ['UAK', 'Ukrainian karbovanets'],
  ['UGS', 'Ugandan shilling (1966–1987)'],
  ['UGX', 'Ugandan shilling'],
  ['USD', 'US Dollar', '$', '$'],
  ['USN', 'US dollar (next day)'],
  ['USS', 'US dollar (same day)'],
  ['UYI', 'Uruguayan peso (indexed units)'],
  ['UYP', 'Uruguayan peso (1975–1993)'],
  ['UYU', 'Uruguayan peso', '$U', '$'],
  ['UZS', 'Uzbekistan som', 'лв'],
  ['VEB', 'Venezuelan bolívar (1871–2008)'],
  ['VEF', 'Venezuelan bolívar', 'Bs', 'Bs'],
  ['VND', 'Vietnamese dong', '₫', '₫'],
  ['VNN', 'Vietnamese dong (1978–1985)'],
  ['VUV', 'Vanuatu vatu'],
  ['WST', 'Samoan tala'],
  ['XAF', 'CFA franc BEAC', 'FCFA', 'FCFA'],
  ['XAG', 'troy ounce of silver'],
  ['XAU', 'troy ounce of gold'],
  ['XBA', 'European composite unit'],
  ['XBB', 'European monetary unit'],
  ['XBC', 'European unit of account (XBC)'],
  ['XBD', 'European unit of account (XBD)'],
  ['XCD', 'East Caribbean dollar', '$', 'EC$'],
  ['XCG', 'Caribbean guilder', 'ƒ', 'CMg'],
  ['XDR', 'special drawing rights'],
  ['XEU', 'European code unit'],
  ['XFO', 'French gold franc'],
  ['XFU', 'French UIC-franc'],
  ['XOF', 'CFA franc BCEAO', 'CFA', 'CFA'],
  ['XPD', 'troy ounce of palladium'],
  ['XPF', 'CFP franc', 'CFPF', 'CFPF'],
  ['XPT', 'troy ounce of platinum'],
  ['XRE', 'RINET Funds Unit'],
  ['XSU', 'Sucre'],
  ['XTS', 'Testing Currency Unit'],
  ['XUA', 'ADB Unit Of Account'],
  ['XXX', '(unknown unit of code)'],
  ['YDD', 'Yemeni Dinar'],
  ['YER', 'Yemeni Rial', '﷼'],
  ['YUD', 'Yugoslavian Hard Dinar (1966–1990)'],
  ['YUM', 'Yugoslavian New Dinar (1994–2002)'],
  ['YUN', 'Yugoslavian Convertible Dinar (1990–1992)'],
  ['YUR', 'Yugoslavian Reformed Dinar (1992–1993)'],
  ['ZAL', 'South African Rand (financial)'],
  ['ZAR', 'South African Rand', 'R', 'R'],
  ['ZMK', 'Zambian Kwacha (1968–2012)'],
  ['ZMW', 'Zambian Kwacha', 'ZK', 'ZK'],
  ['ZRN', 'Zairean New Zaire (1993–1998)'],
  ['ZRZ', 'Zairean Zaire (1971–1993)'],
  ['ZWD', 'Zimbabwean Dollar (1980–2008)', 'Z$'],
  ['ZWL', 'Zimbabwean Dollar (2009)'],
  ['ZWR', 'Zimbabwean Dollar (2008)'],
];

export const currencies: Record<string, CurrencyObject> = currencyInfo.reduce(
  (acc: Record<string, CurrencyObject>, [code, name, ...symbols]) => {
    acc[code] = makeCurrencyObject(code, name, ...symbols);
    return acc;
  },
  {}
);
